const DATE_FMT = new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short', year: 'numeric' });
const DATE_TIME_FMT = new Intl.DateTimeFormat('en-SG', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

/** `2026-04-06` -> `6 Apr 2026`. Date-only strings are read as local dates, not UTC. */
export function formatDate(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return DATE_FMT.format(new Date(y, m - 1, d));
}

const DAY_MONTH_FMT = new Intl.DateTimeFormat('en-SG', { day: 'numeric', month: 'short' });

/** `2026-08-14` -> `14 Aug`, for dates whose year is plain from context. */
export function formatDayMonth(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return DAY_MONTH_FMT.format(new Date(y, m - 1, d));
}

export function formatDateTime(iso: string): string {
  return DATE_TIME_FMT.format(new Date(iso));
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** How long ago, for things that just happened: `just now`, `5 minutes ago`, `3 hours ago`, `2 days ago`. */
export function formatAgo(iso: string, now: Date = new Date()): string {
  const ms = Math.max(0, now.getTime() - new Date(iso).getTime());
  if (ms < MINUTE) return 'just now';
  if (ms < HOUR) return `${pluralise(Math.floor(ms / MINUTE), 'minute')} ago`;
  if (ms < DAY) return `${pluralise(Math.floor(ms / HOUR), 'hour')} ago`;
  return `${pluralise(Math.floor(ms / DAY), 'day')} ago`;
}

/** How long something took, for "answered 1 minute later": `under a minute`, `25 minutes`, `3 hours`, `2 days`. */
export function formatSpan(fromIso: string, toIso: string): string {
  const ms = Math.max(0, new Date(toIso).getTime() - new Date(fromIso).getTime());
  if (ms < MINUTE) return 'under a minute';
  if (ms < HOUR) return pluralise(Math.floor(ms / MINUTE), 'minute');
  if (ms < DAY) return pluralise(Math.floor(ms / HOUR), 'hour');
  return pluralise(Math.floor(ms / DAY), 'day');
}

/** Keeps the precision the report printed: 1.28 stays 1.28, 16420 becomes 16,420. */
export function formatValue(value: number): string {
  const decimals = Math.abs(value) >= 100 ? 0 : (value.toString().split('.')[1]?.length ?? 0);
  return value.toLocaleString('en-SG', { minimumFractionDigits: Math.min(decimals, 4), maximumFractionDigits: 4 });
}

export function formatChange(from: number, to: number): string {
  const diff = to - from;
  const sign = diff > 0 ? '+' : '';
  return `${sign}${formatValue(Number(diff.toFixed(4)))}`;
}

export function pluralise(count: number, one: string, many = `${one}s`): string {
  return `${count.toLocaleString('en-SG')} ${count === 1 ? one : many}`;
}
